<script setup>
import { ref } from 'vue'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { Format } from '@/models/utils/FormatUtils.js'
import ObjectViewerModal from '@/components/Alerts/ObjectViewerModal.vue'
import CopyTextModal from '@/components/Alerts/CopyTextModal.vue'

const showCopyModal = ref(false)
const dialogTitle = ref('')
const plainDialogMessage = ref('')
const formattedDialogMessage = ref('')

const table = ref(null)
const showObjectModal = ref(false)
const activeData = ref({})

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'email-logs/all-logs',
		endpointDisplayName: 'Email Logs',
		initialSortColumn: 'updated_at',
		initialSortDirection: 'desc',
		searchPlaceholder: 'Search',
		fullTextSearch: false,
		columns: [
			{ field: "id", title: "ID", sort: true, width: '1%' },
			{ field: "status", title: "Status", sort: true, search: true },
			{ field: "created_at", title: "Created", sort: true, width: '1%' },
			{ field: "updated_at", title: "Updated", sort: true, width: '1%' },
			{ field: "email_type", title: "Email Type/Source", sort: false, search: true },
			{ field: "sent_by_user_id", title: "Sent By", sort: true, width: '1%', search: true },
			{ field: "recipients", title: "Recipients", sort: false, search: true, minWidth: '400px' },
			{ field: "email_info", title: "Information", sort: true, search: true },
			{ field: "attachment_name", title: "Attachment", sort: true, width: '1%', search: true },
		],
	})
]

function showTextDialog(title, message) {
	dialogTitle.value = title
	plainDialogMessage.value = message
	formattedDialogMessage.value = message
	showCopyModal.value = true
}

function showErrorMessage(p_email_info)
{
	if (p_email_info && p_email_info.error_message)
	{
		showTextDialog('Failed', p_email_info.error_message, null, 2)
	}
	else
	{
		showTextDialog('Failed', 'Unknown Error', null, 2)
	}
}

function showRecipients(p_recipients)
{
	let recipients = '';

	for (let i = 0; i < p_recipients.length; i++)
	{
		if (p_recipients[i].name)
		{
			recipients += p_recipients[i].name + ' (' +	p_recipients[i].address + ")\r\n";
		}
		else
		{
			recipients += p_recipients[i].address + "\r\n";
		}
	}

	showTextDialog('Recipients', recipients)
}

/**
 * Shows or hides the Object Viewer Modal
 * @param {boolean} p_show True to show modal
 * @param p_data
 */
function toggleObjectViewerModal(p_show, p_data)
{
	if (p_show)
	{
		showObjectModal.value = true
		activeData.value = p_data
	}
	else
	{
		showObjectModal.value = false;
	}
}

</script>

<template>
	<PortalDataTable
		ref="table"
		:endpoints="endpoints"
	>
		<template #status="data">
			<div v-if="data.value.status.toLowerCase() !== 'failed'" class="capitalize">
				{{ data.value.status }}
			</div>
			<div v-else class="capitalize line-clamp-5 !text-error clickable-text overflow-ellipsis" @click="showErrorMessage(data.value.email_info)">
				{{ data.value.status }}
			</div>
		</template>

		<template #created_at="data">
			{{ Format.date(data.value.created_at) }}
		</template>

		<template #updated_at="data">
			{{ Format.date(data.value.updated_at) }}
		</template>

		<template #recipients="data">
			<div class="line-clamp-4 clickable-text overflow-ellipsis" @click="showRecipients(data.value.recipients)">
				<div v-for="(recipient, index) in data.value.recipients" :key="index">
					<div v-if="recipient.name"><b>{{ recipient.name }}</b> ({{ recipient.address }})</div>
					<div v-else>{{ recipient.address }}</div>
				</div>
			</div>
		</template>

		<template #email_info="data">
			<div class="line-clamp-1 clickable-text"  @click="toggleObjectViewerModal(true, data.value.email_info)">
				Click to view
			</div>
		</template>

		<template #sent_by_user_id="data">
			<div class="flex flex-row gap-1">
				<div class="whitespace-nowrap">{{ data.value.user_name }}</div>
				<div v-if="data.value.sent_by_user_id && data.value.sent_by_user_id > 0">({{ data.value.sent_by_user_id }})</div>
				<div v-else>Portal</div>
			</div>
		</template>
	</PortalDataTable>

	<ObjectViewerModal
		:data="activeData"
		:show="showObjectModal"
		:title="dialogTitle"
		@close="toggleObjectViewerModal(false)"
	/>

	<CopyTextModal
		:show="showCopyModal"
		:title="dialogTitle"
		:message="plainDialogMessage"
		:formattedMessage="formattedDialogMessage"
		@close="showCopyModal=false"
	/>
</template>

<style scoped>

</style>